import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  Activity,
  TrendingUp,
  Building2,
  Calendar,
  Filter,
  RefreshCw,
  AlertTriangle,
  Download,
  Monitor,
  Smartphone,
  BarChart3,
  LineChart,
  Table,
} from 'lucide-react';
import {
  LineChart as ReLineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  assets: 'Assets',
  work_orders: 'Work Orders',
  site_visits: 'Site Visits',
  action_plans: 'Action Plans',
  alerts: 'Alerts',
  analytics: 'Analytics',
  commercial: 'Commercial',
  finance: 'Finance',
  admin: 'Admin',
  settings: 'Settings',
  auth: 'Authentication',
};

const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7days' },
  { label: 'Last 30 days', value: '30days' },
  { label: 'Last 90 days', value: '90days' },
  { label: 'Custom', value: 'custom' },
];

export default function UserActivityAnalyticsPage() {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [orgInsights, setOrgInsights] = useState(null);
  const [activityLogs, setActivityLogs] = useState({ logs: [], total_count: 0, page: 1 });
  const [organizations, setOrganizations] = useState([]);
  
  // Filters
  const [datePreset, setDatePreset] = useState('30days');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [logsPage, setLogsPage] = useState(1);

  // Calculate dates based on preset
  const getDateRange = useCallback(() => {
    const now = new Date();
    let from, to;
    to = now.toISOString();
    
    switch (datePreset) {
      case 'today':
        from = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case '7days':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '90days':
        from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'custom':
        from = dateFrom ? new Date(dateFrom).toISOString() : new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        to = dateTo ? new Date(dateTo).toISOString() : new Date().toISOString();
        break;
      default: // 30days
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }
    return { from, to };
  }, [datePreset, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      
      const params = new URLSearchParams();
      params.append('date_from', from);
      params.append('date_to', to);
      if (selectedOrg !== 'all') params.append('org_id', selectedOrg);
      if (selectedRole !== 'all') params.append('role', selectedRole);
      if (selectedModule !== 'all') params.append('module_name', selectedModule);
      if (selectedSource !== 'all') params.append('source', selectedSource);
      
      const [analyticsRes, trendsRes, orgsInsightsRes, logsRes, orgsRes] = await Promise.all([
        axios.get(`${API}/analytics/user-activity?${params.toString()}`),
        axios.get(`${API}/analytics/user-activity/trends?${params.toString()}`),
        axios.get(`${API}/analytics/user-activity/orgs?date_from=${from}&date_to=${to}`),
        axios.get(`${API}/analytics/user-activity/logs?${params.toString()}&page=${logsPage}&page_size=20`),
        axios.get(`${API}/organizations`),
      ]);
      
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
      setOrgInsights(orgsInsightsRes.data);
      setActivityLogs(logsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, selectedOrg, selectedRole, selectedModule, selectedSource, logsPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const { from, to } = getDateRange();
      const response = await axios.get(`${API}/analytics/user-activity/export?date_from=${from}&date_to=${to}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-activity-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  // Prepare chart data
  const moduleUsageData = analytics?.module_usage ? 
    Object.entries(analytics.module_usage).map(([name, count], idx) => ({
      name: MODULE_LABELS[name] || name,
      value: count,
      fill: CHART_COLORS[idx % CHART_COLORS.length]
    })) : [];

  const sourceData = analytics?.source_breakdown ? 
    Object.entries(analytics.source_breakdown).map(([name, count]) => ({
      name: name === 'web' ? 'Web' : 'Mobile',
      value: count,
    })) : [];

  if (!hasRole(['superadmin', 'platform_admin'])) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-500">User Activity Analytics is only available to Platform Administrators.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-activity-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Activity Analytics</h1>
            <p className="text-slate-500">Track product adoption, feature usage, and user behavior</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Date Range</Label>
                <Select value={datePreset} onValueChange={setDatePreset}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-date-preset">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {datePreset === 'custom' && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">From</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-[140px]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">To</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-[140px]"
                    />
                  </div>
                </>
              )}
              
              <div className="space-y-1">
                <Label className="text-xs">Organization</Label>
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-org">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="superadmin">Super Admin</SelectItem>
                    <SelectItem value="platform_admin">Platform Admin</SelectItem>
                    <SelectItem value="org_admin">Org Admin</SelectItem>
                    <SelectItem value="org_user">Org User</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Module</Label>
                <Select value={selectedModule} onValueChange={setSelectedModule}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-module">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modules</SelectItem>
                    {Object.entries(MODULE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Source</Label>
                <Select value={selectedSource} onValueChange={setSelectedSource}>
                  <SelectTrigger className="w-[120px]" data-testid="filter-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Users Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-dau">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Daily Active Users</p>
                  <p className="text-3xl font-bold text-slate-900">{analytics?.active_users?.dau || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Today</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-wau">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Weekly Active Users</p>
                  <p className="text-3xl font-bold text-slate-900">{analytics?.active_users?.wau || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Last 7 days</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-mau">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Monthly Active Users</p>
                  <p className="text-3xl font-bold text-slate-900">{analytics?.active_users?.mau || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Last 30 days</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-events">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Events</p>
                  <p className="text-3xl font-bold text-slate-900">{(analytics?.total_events || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-400 mt-1">In selected period</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="trends">
          <TabsList>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <LineChart className="w-4 h-4 mr-2" />
              Activity Trends
            </TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules">
              <BarChart3 className="w-4 h-4 mr-2" />
              Module Usage
            </TabsTrigger>
            <TabsTrigger value="orgs" data-testid="tab-orgs">
              <Building2 className="w-4 h-4 mr-2" />
              Organization Insights
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <Table className="w-4 h-4 mr-2" />
              Activity Logs
            </TabsTrigger>
          </TabsList>

          {/* Activity Trends Tab */}
          <TabsContent value="trends" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Activity Over Time */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Activity Over Time</CardTitle>
                  <CardDescription>Events and unique users per day</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <ReLineChart data={trends?.activity_trend || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                          <YAxis stroke="#64748b" fontSize={11} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                            formatter={(value, name) => [value, name === 'events' ? 'Events' : 'Unique Users']}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="events" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Events" />
                          <Line type="monotone" dataKey="unique_users" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Unique Users" />
                        </ReLineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Source Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Platform vs Mobile</CardTitle>
                  <CardDescription>Activity by source</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sourceData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          <Cell fill="#0ea5e9" />
                          <Cell fill="#8b5cf6" />
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-sky-500" />
                      <span className="text-sm text-slate-600">
                        {analytics?.source_breakdown?.web || 0} Web
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-5 h-5 text-purple-500" />
                      <span className="text-sm text-slate-600">
                        {analytics?.source_breakdown?.mobile || 0} Mobile
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Module Usage Tab */}
          <TabsContent value="modules" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Module Usage Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Module Usage Frequency</CardTitle>
                  <CardDescription>Events per module</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={moduleUsageData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={11} />
                        <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={11} width={100} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {moduleUsageData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Action Types */}
              <Card>
                <CardHeader>
                  <CardTitle>Action Types</CardTitle>
                  <CardDescription>Breakdown by action</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics?.action_breakdown || {}).map(([action, count], idx) => (
                      <div key={action} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                          />
                          <span className="text-sm text-slate-600 capitalize">{action.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ 
                                width: `${(count / (analytics?.total_events || 1)) * 100}%`,
                                backgroundColor: CHART_COLORS[idx % CHART_COLORS.length]
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-16 text-right">
                            {count.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Role Distribution */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Activity by Role</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(analytics?.role_breakdown || {}).map(([role, count]) => {
                      const roleLabels = {
                        superadmin: 'Super Admin',
                        platform_admin: 'Platform Admin',
                        org_admin: 'Org Admin',
                        org_user: 'Org User',
                        viewer: 'Viewer'
                      };
                      return (
                        <div key={role} className="p-4 bg-slate-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-slate-900">{count.toLocaleString()}</p>
                          <p className="text-sm text-slate-500">{roleLabels[role] || role}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Organization Insights Tab */}
          <TabsContent value="orgs" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Most Active Orgs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Most Active Organizations
                  </CardTitle>
                  <CardDescription>Top 5 by activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(orgInsights?.most_active || []).map((org, idx) => (
                      <div key={org.org_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-bold">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{org.org_name}</p>
                            <p className="text-xs text-slate-500">{org.unique_users} users</p>
                          </div>
                        </div>
                        <Badge variant="secondary">{org.total_events.toLocaleString()} events</Badge>
                      </div>
                    ))}
                    {(!orgInsights?.most_active || orgInsights.most_active.length === 0) && (
                      <p className="text-center text-slate-500 py-4">No activity data</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Least Active / Churn Risk */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Least Active (Churn Risk)
                  </CardTitle>
                  <CardDescription>Organizations with low engagement</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(orgInsights?.least_active || []).map((org) => (
                      <div key={org.org_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium text-slate-900">{org.org_name}</p>
                          <p className="text-xs text-amber-600">
                            Last active: {org.last_activity ? new Date(org.last_activity).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          {org.total_events} events
                        </Badge>
                      </div>
                    ))}
                    {(orgInsights?.inactive_orgs || []).map((org) => (
                      <div key={org.org_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <p className="font-medium text-slate-900">{org.org_name}</p>
                          <p className="text-xs text-red-600">No activity in period</p>
                        </div>
                        <Badge variant="destructive">Inactive</Badge>
                      </div>
                    ))}
                    {(!orgInsights?.least_active || orgInsights.least_active.length === 0) && 
                     (!orgInsights?.inactive_orgs || orgInsights.inactive_orgs.length === 0) && (
                      <p className="text-center text-slate-500 py-4">All organizations are active</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Organization Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                      <p className="text-3xl font-bold text-emerald-700">{orgInsights?.total_active_orgs || 0}</p>
                      <p className="text-sm text-emerald-600">Active Organizations</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                      <p className="text-3xl font-bold text-red-700">{orgInsights?.total_inactive_orgs || 0}</p>
                      <p className="text-sm text-red-600">Inactive Organizations</p>
                    </div>
                    <div className="p-4 bg-sky-50 rounded-lg text-center border border-sky-200">
                      <p className="text-3xl font-bold text-sky-700">
                        {orgInsights?.all_orgs?.reduce((sum, o) => sum + o.unique_users, 0) || 0}
                      </p>
                      <p className="text-sm text-sky-600">Active Users (All Orgs)</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center border border-purple-200">
                      <p className="text-3xl font-bold text-purple-700">
                        {orgInsights?.all_orgs?.reduce((sum, o) => sum + o.total_events, 0)?.toLocaleString() || 0}
                      </p>
                      <p className="text-sm text-purple-600">Total Events (All Orgs)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="logs" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>User Activity Logs</CardTitle>
                <CardDescription>
                  Showing {activityLogs.logs?.length || 0} of {activityLogs.total_count?.toLocaleString() || 0} events
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Timestamp</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Role</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Module</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Action</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(activityLogs.logs || []).map((log) => (
                        <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-sm font-medium text-slate-900">{log.user_name}</p>
                              <p className="text-xs text-slate-500">{log.user_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-xs">
                              {log.role?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-slate-600">
                              {MODULE_LABELS[log.module_name] || log.module_name}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary" className="text-xs capitalize">
                              {log.action_type?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              {log.source === 'web' ? (
                                <Monitor className="w-4 h-4 text-slate-400" />
                              ) : (
                                <Smartphone className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-600">{log.source}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!activityLogs.logs || activityLogs.logs.length === 0) && (
                    <p className="text-center text-slate-500 py-8">No activity logs found</p>
                  )}
                </div>

                {/* Pagination */}
                {activityLogs.total_pages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-500">
                      Page {activityLogs.page} of {activityLogs.total_pages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={logsPage === 1}
                        onClick={() => setLogsPage(logsPage - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={logsPage >= activityLogs.total_pages}
                        onClick={() => setLogsPage(logsPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
